import { useState, useEffect } from 'react';
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router';

import { ATSConnectHiringRequestTestIds } from 'data-testids/ATS';
import { ConnectCandidateRowSkeleton } from './CandidateList/CandidateRow/components/ConnectCandidateRowSkeleton';
import { ConnectCandidatesList } from './CandidateList/ConnectCandidatesList';
import { Container } from './styles/hiringRequestStyles';
import { HiringRequestsFormModal } from './CandidateImport/HiringRequestsFormModal';
import { useQueryGetHiringRequestListData } from './api/hiringRequestQueries';
import { LoadMoreButton } from './components';
import auth from 'utils/auth';
import { Alert } from 'sharedComponents/Alert';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';

import { HiringRequestListHeader } from './CandidateList/HiringRequestListHeader/HiringRequestListHeader';

const unauthorizedImportErrorKey = 'unauthorizedImportError';
const unauthorizedImportValue = 'attempted';

export const HiringRequestsLandingPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const candidateId = Number(queryParams.get('candidate_id'));
  const [currentModalOpen, setCurrentModalOpen] = useState<'add'>(null);
  const districtId = auth.getUser()?.profile.district.id;
  const [snackbarError, setSnackbarError] = useState<{
    open: boolean;
    message: string;
    duration?: number;
    dataTestId?: string;
    onClose?: () => void;
  }>({
    open: false,
    message: '',
    duration: 5000,
  });
  const isExactHiringRequestRoute = useRouteMatch('/hiring-requests')?.isExact;
  const { data, error, isError, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useQueryGetHiringRequestListData(districtId);

  const allResults = data?.pages.flatMap((page) => page.results) ?? [];
  const totalCount = data?.pages[0]?.count ?? 0;

  const handleClose = () => {
    setCurrentModalOpen(null);
    if (auth.isSchoolUser()) {
      history.replace('/principal/hiring-requests');
    } else {
      history.replace('/hiring-requests');
    }
  };

  const displaySnackbarError = (
    message: string,
    duration?: number,
    dataTestId?: string,
    onClose?: () => void
  ) => {
    setSnackbarError({
      open: true,
      message,
      duration: duration ?? 5000,
      dataTestId,
      onClose,
    });
  };

  const handleLoadMoreCandidates = async () => {
    try {
      await fetchNextPage();
    } catch (error) {
      const message = getAxiosErrorMessageByStatusCode(error);
      displaySnackbarError(
        `Failed to load more candidates: ${message}`,
        5000,
        ATSConnectHiringRequestTestIds.LOAD_MORE_ERROR
      );
    }
  };

  useEffect(() => {
    const hasUnauthorizedImportError =
      sessionStorage.getItem(unauthorizedImportErrorKey) === unauthorizedImportValue;
    const isValidCandidateId = Number.isInteger(candidateId) && candidateId > 0;
    const errorMessage = auth.isATSFullDistrict()
      ? 'Submitting hiring requests is only available to school admins and selected super admins at this time.'
      : 'Submitting hiring requests is only available to school users and selected super admins at this time.';

    if (isValidCandidateId && !hasUnauthorizedImportError) {
      setCurrentModalOpen('add');
    }

    if (hasUnauthorizedImportError) {
      displaySnackbarError(
        errorMessage,
        5000,
        ATSConnectHiringRequestTestIds.UNAUTHORIZED_IMPORT_CANDIDATE_ERROR,
        () => {
          setSnackbarError((prev) => ({ ...prev, open: false }));
          sessionStorage.removeItem(unauthorizedImportErrorKey);
        }
      );
    }
  }, [candidateId]);

  if (!auth.hasConnectIntegrationModule()) {
    if (auth.isSchoolUser()) {
      return <Redirect to={`/principal/candidates`} />;
    }
    const redirectPrefix = auth.isSchoolAdmin() ? '/school' : '/district';
    return <Redirect to={`${redirectPrefix}/home/`} />;
  } else {
    if (auth.isSchoolUser() && isExactHiringRequestRoute) {
      return <Redirect to={`/principal/hiring-requests`} />;
    }
  }

  if (!auth.isSchoolAdmin() && !auth.isSchoolUser() && !auth.isSuperAdminUser() && candidateId) {
    sessionStorage.setItem(unauthorizedImportErrorKey, unauthorizedImportValue);
    return <Redirect to="/hiring-requests" />;
  }

  if (isError) {
    const message = getAxiosErrorMessageByStatusCode(error);
    return <Alert type="error">{message}</Alert>;
  }

  return (
    <>
      <Container>
        <HiringRequestListHeader />

        {isLoading && allResults.length === 0 ? (
          <ConnectCandidateRowSkeleton />
        ) : (
          <>
            <ConnectCandidatesList results={allResults} />
            <LoadMoreButton
              onClick={handleLoadMoreCandidates}
              isLoading={isFetchingNextPage}
              hasMore={hasNextPage ?? false}
              totalLoaded={allResults.length}
              totalCount={totalCount}
              dataTestId={ATSConnectHiringRequestTestIds.LOAD_MORE_BUTTON}
            />
          </>
        )}
      </Container>
      <HiringRequestsFormModal
        isOpen={currentModalOpen === 'add'}
        candidateId={candidateId}
        handleClose={handleClose}
      />
      <ErrorSnackbar
        open={snackbarError.open}
        onClose={snackbarError.onClose}
        message={snackbarError.message}
        duration={snackbarError.duration}
        dataTestId={snackbarError.dataTestId}
      />
    </>
  );
};
